/**
 * The double-six tile set: construction and pip arithmetic.
 *
 * Canonical tile-id contract (see @domino/types): ids are 0-27 and each pair
 * is stored high-first (high >= low). This module owns the exact id
 * enumeration order so every consumer agrees.
 */

import type { Tile } from "@domino/types";

/** Number of tiles in the double-six set. */
export const TILE_SET_SIZE = 28;

/** Highest pip count on one half of a double-six tile. */
export const MAX_PIP = 6;

/**
 * Build the 28-tile double-six set: every pair (a, b) with 0 <= a <= b <= 6.
 *
 * Canonical enumeration order (stable contract - never change): iterate the
 * lower half `a` from 0 to 6, and for each `a` iterate the higher half `b`
 * from `a` to 6. Tile ids are assigned 0-27 in exactly that order:
 *
 *   id 0  = (0,0)   id 1  = (0,1)   id 2  = (0,2)   id 3  = (0,3)
 *   id 4  = (0,4)   id 5  = (0,5)   id 6  = (0,6)
 *   id 7  = (1,1)   id 8  = (1,2)   id 9  = (1,3)   id 10 = (1,4)
 *   id 11 = (1,5)   id 12 = (1,6)
 *   id 13 = (2,2)   id 14 = (2,3)   id 15 = (2,4)   id 16 = (2,5)
 *   id 17 = (2,6)
 *   id 18 = (3,3)   id 19 = (3,4)   id 20 = (3,5)   id 21 = (3,6)
 *   id 22 = (4,4)   id 23 = (4,5)   id 24 = (4,6)
 *   id 25 = (5,5)   id 26 = (5,6)
 *   id 27 = (6,6)
 *
 * Each pair is stored in canonical order (high >= low); `isDouble` and
 * `pipValue` are derived. Returns a fresh array of frozen tiles on every
 * call (the full set sums to 168 pips; each number appears on exactly 7
 * tiles, counting a double's pips twice).
 */
export function buildTileSet(): Tile[] {
  const tiles: Tile[] = [];
  let id = 0;
  for (let low = 0; low <= MAX_PIP; low++) {
    for (let high = low; high <= MAX_PIP; high++) {
      tiles.push(
        Object.freeze({
          id,
          high,
          low,
          isDouble: high === low,
          pipValue: high + low,
        }),
      );
      id += 1;
    }
  }
  return tiles;
}

/**
 * Sum of `pipValue` over the given tiles (blank = 0). The full double-six
 * set sums to 168.
 */
export function totalPips(tiles: readonly Tile[]): number {
  let total = 0;
  for (const tile of tiles) total += tile.pipValue;
  return total;
}

/**
 * Recover `(high, low)` from a canonical tile id without building the set.
 * Inverts the enumeration order documented on `buildTileSet`: ids are grouped
 * into blocks by lower half (the low-0 block holds 7 ids 0-6, the low-1 block
 * holds 6 ids 7-12, and so on down to the single id 27). Throws a plain
 * Error when `tileId` is not an integer in [0, 27].
 */
function halvesOf(tileId: number): { high: number; low: number } {
  if (!Number.isInteger(tileId) || tileId < 0 || tileId >= TILE_SET_SIZE) {
    throw new Error(
      `tileId must be an integer in [0, ${TILE_SET_SIZE - 1}], got ${String(tileId)}`,
    );
  }
  let low = 0;
  let blockStart = 0;
  let blockSize = MAX_PIP + 1;
  while (tileId >= blockStart + blockSize) {
    blockStart += blockSize;
    blockSize -= 1;
    low += 1;
  }
  return { high: low + (tileId - blockStart), low };
}

/**
 * Pip value (`high + low`) of a canonical tile id, derived without building
 * the set. Throws a plain Error when `tileId` is not an integer in [0, 27].
 */
export function pipValueOf(tileId: number): number {
  const { high, low } = halvesOf(tileId);
  return high + low;
}
